/**
 * Werving — /werving/vacature/:id: pipeline van één vacature (Homerun "Candidates").
 *
 * Kanban per fase (slepen = fase verzetten, met ongedaan maken; afwijzen/aannemen eerst bevestigen) of een
 * tabel, plus zoeken. Kaartje: naam, wanneer binnen, portfolio, en voor jou "Te beoordelen" of je oordeel.
 * Alleen met recht Werving (bewerken) kun je slepen; scores staan hier nooit (blind scoren, in de API).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Checkbox, CircularProgress, FormControlLabel, InputAdornment, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import LinkIcon from '@mui/icons-material/Link';
import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { bevestig } from '../../utils/bevestig';
import { wervingApi } from './wervingApi';
import VacatureDialoog from './VacatureDialoog';
import { Avatar, FaseChip } from './WervingUi';
import {
  ACCENT, ACCENT_ZACHT, EIND_FASEN, INKT, KOLOM_BG, LIJN, OORDEEL_LABEL, STATUS_LABEL, STIL, alleenTeBeoordelen, dagenGeleden, faseKleur,
  filterStagetype, gesprekLabel, groepeerPerFase, zoek,
} from './wervingLogica';

function JouwStatus({ s }) {
  if (s.te_beoordelen) {
    return <Box component="span" sx={{ fontSize: 11.5, fontWeight: 700, color: ACCENT, border: `1px solid ${ACCENT}`, borderRadius: 10, px: 1, py: '1px' }}>Te beoordelen</Box>;
  }
  if (s.mijn_oordeel) return <Typography component="span" sx={{ fontSize: 12, color: STIL }}>Jij: {OORDEEL_LABEL[s.mijn_oordeel].toLowerCase()}</Typography>;
  return null;
}

/** Kwaliteitssignaal (alleen als de API het meestuurt = jij mag het zien) of anders het aantal beoordelingen. */
function Signaal({ s }) {
  if (s.signaal) {
    const { goed, af, gemiddelde } = s.signaal;
    return (
      <Tooltip title={`Zichtbare beoordelingen (${s.signaal.fase}): ${goed} goedgekeurd, ${af} afgekeurd${gemiddelde != null ? `, gemiddeld ${gemiddelde}` : ''}`}>
        <Typography sx={{ fontSize: 11.5, color: STIL }}>👍 {goed} · 👎 {af}{gemiddelde != null ? ` · ${gemiddelde}` : ''}</Typography>
      </Tooltip>
    );
  }
  return <Typography sx={{ fontSize: 11.5, color: STIL }}>{s.aantal_beoordelingen}/{s.aantal_beoordelaars} beoordeeld</Typography>;
}

function Kaartje({ s, kanSlepen, onOpen, stageLabel }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: String(s.id), data: { s }, disabled: !kanSlepen });
  return (
    <Box ref={setNodeRef} {...attributes} {...listeners} onClick={() => onOpen(s.id)}
      sx={{ bgcolor: '#fff', border: `1px solid ${LIJN}`, borderRadius: 2.5, p: 1.5, mb: 1, cursor: kanSlepen ? 'grab' : 'pointer',
        transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined, opacity: isDragging ? 0.6 : 1,
        position: 'relative', zIndex: isDragging ? 10 : 'auto', '&:hover': { borderColor: '#d5dae1' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar naam={s.naam} maat={28} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: INKT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.naam}</Typography>
          <Typography sx={{ fontSize: 12, color: STIL, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {dagenGeleden(s.ontvangen_op)}{s.portfolio && <LinkIcon sx={{ fontSize: 14, ml: 0.5 }} titleAccess="Portfolio" />}
          </Typography>
        </Box>
      </Box>
      {stageLabel && <Typography sx={{ fontSize: 12, color: STIL, mt: 0.5 }}>{stageLabel}</Typography>}
      {s.fase === 'gesprek' && s.gesprek_op && (
        <Typography sx={{ fontSize: 12, color: INKT, mt: 0.5 }}>Gesprek {gesprekLabel(s.gesprek_op)}</Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, pt: 1, borderTop: `1px solid ${LIJN}`, minHeight: 22 }}>
        <JouwStatus s={s} />
        <Box sx={{ flex: 1 }} />
        <Signaal s={s} />
      </Box>
    </Box>
  );
}

function Kolom({ fase, lijst, kanSlepen, onOpen, gemarkeerd, stageLabels }) {
  const { setNodeRef, isOver } = useDroppable({ id: fase.sleutel });
  if (!lijst.length) {
    // Lege kolom ingeklapt: smal, maar nog steeds een plek om naartoe te slepen
    return (
      <Box ref={setNodeRef} title={`${fase.label}: leeg`} sx={{ flex: '0 0 44px', minHeight: 'calc(100vh - 300px)', borderRadius: 3,
        bgcolor: isOver ? ACCENT_ZACHT : KOLOM_BG, border: `1px solid ${gemarkeerd ? '#cfd6e0' : LIJN}`, display: 'flex', justifyContent: 'center', pt: 1.5 }}>
        <Typography sx={{ writingMode: 'vertical-rl', fontSize: 12.5, fontWeight: 600, color: faseKleur(fase.sleutel).tekst, opacity: 0.75, whiteSpace: 'nowrap' }}>
          {fase.label} <Box component="span" sx={{ fontWeight: 400 }}>0</Box>
        </Typography>
      </Box>
    );
  }
  return (
    <Box ref={setNodeRef} sx={{ flex: '0 0 264px', minHeight: 'calc(100vh - 300px)', bgcolor: isOver ? ACCENT_ZACHT : KOLOM_BG,
      border: `1px solid ${gemarkeerd ? '#cfd6e0' : LIJN}`, borderRadius: 3, p: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 0.5, pb: 1, mb: 1, borderBottom: `2px solid ${faseKleur(fase.sleutel).stip}` }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: faseKleur(fase.sleutel).stip }} />
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: faseKleur(fase.sleutel).tekst }}>
          {fase.label} <Box component="span" sx={{ color: STIL, fontWeight: 400 }}>{lijst.length}</Box>
        </Typography>
      </Box>
      {lijst.map((s) => <Kaartje key={s.id} s={s} kanSlepen={kanSlepen} onOpen={onOpen} stageLabel={stageLabels?.[s.stagetype]} />)}
    </Box>
  );
}

export default function VacaturePipeline() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [fout, setFout] = useState(null);
  const [q, setQ] = useState('');
  const [weergave, setWeergave] = useState(() => { try { return localStorage.getItem('werving.weergave') || 'kanban'; } catch { return 'kanban'; } });
  const [bewerkOpen, setBewerkOpen] = useState(false);
  const [alleenMijn, setAlleenMijn] = useState(() => params.get('mijn') === '1');
  const [stagetype, setStagetype] = useState(() => params.get('stagetype') || '');
  const [disciplines, setDisciplines] = useState([]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const laad = useCallback(() => wervingApi.vacature(id).then((x) => { setD(x); setFout(null); }).catch((e) => setFout(e)), [id]);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => registreerOngedaanSneltoets(), []);
  useEffect(() => { wervingApi.disciplines().then((x) => setDisciplines(x.disciplines || [])).catch(() => {}); }, []);
  useEffect(() => { try { localStorage.setItem('werving.weergave', weergave); } catch { /* geen opslag */ } }, [weergave]);

  const gefilterd = useMemo(() => alleenTeBeoordelen(filterStagetype(zoek(d?.sollicitaties, q), stagetype || null), alleenMijn),
    [d, q, alleenMijn, stagetype]);
  const stageLabels = useMemo(() => Object.fromEntries((d?.stagetypes || []).map((t) => [t.sleutel, t.label])), [d]);
  const perFase = useMemo(() => groepeerPerFase(gefilterd, d?.fasen), [gefilterd, d]);
  const gemarkeerd = params.get('fase');
  const open = (sid) => navigate(`/werving/sollicitatie/${sid}`);

  const verzet = async (s, naar) => {
    if (!d?.kan_bewerken || s.fase === naar) return;
    const label = d.fasen.find((f) => f.sleutel === naar)?.label;
    if (EIND_FASEN.includes(naar) && !(await bevestig({
      titel: `${s.naam} naar ${label}?`,
      tekst: naar === 'afgewezen'
        ? 'De procedure stopt; de gegevens worden 4 weken bewaard (1 jaar met toestemming). Er gaat geen mail uit — een afwijzing zet je apart klaar als concept.'
        : 'De sollicitatie wordt als aangenomen gemarkeerd. Er gaat geen mail uit.',
      knop: `Naar ${label}`, gevaarlijk: naar === 'afgewezen',
    }))) return;
    const vorige = s.fase;
    setD((x) => ({ ...x, sollicitaties: x.sollicitaties.map((y) => (y.id === s.id ? { ...y, fase: naar } : y)) }));
    try {
      await wervingApi.fase(s.id, naar, vorige);
      biedOngedaanAan(`${s.naam} → ${label}`, async () => { await wervingApi.fase(s.id, vorige, naar); await laad(); });
      laad();
    } catch (e) { toast.error(e.message); laad(); }
  };

  const onDragEnd = ({ active, over }) => {
    if (!over) return;
    const s = d.sollicitaties.find((x) => String(x.id) === String(active.id));
    if (s) verzet(s, over.id);
  };

  if (fout) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', mt: 10, textAlign: 'center', px: 2 }}>
        <Typography sx={{ fontWeight: 700, color: INKT }}>{fout.status === 404 ? 'Vacature niet gevonden' : 'Kon de vacature niet laden'}</Typography>
        <Typography sx={{ color: STIL, mt: 1 }}>{fout.status === 404 ? 'Of je bent geen beoordelaar van deze vacature.' : fout.message}</Typography>
        <Button onClick={() => navigate('/werving')} sx={{ mt: 2, textTransform: 'none', color: ACCENT }}>Naar Werving</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <Typography role="button" onClick={() => navigate('/werving')} sx={{ fontSize: 13, color: STIL, cursor: 'pointer', mb: 1, '&:hover': { color: INKT } }}>
        ← Werving
      </Typography>
      <PageHeader dense icon="person_search" title={d?.titel || 'Vacature'}
        subtitle={d ? [disciplines.find((x) => x.sleutel === d.discipline)?.label, STATUS_LABEL[d.status],
          `${d.sollicitaties.length} sollicitatie${d.sollicitaties.length === 1 ? '' : 's'}`,
          `${d.beoordelaars_effectief.length} beoordelaars`].filter(Boolean).join(' · ') : ''}
        rightActions={d?.kan_bewerken && (
          <Button size="small" variant="outlined" onClick={() => setBewerkOpen(true)} sx={{ textTransform: 'none', color: ACCENT, borderColor: LIJN }}>Bewerken</Button>
        )} />

      {d?.zonder_recht?.length > 0 && (
        <Typography sx={{ fontSize: 13, color: '#b45309', mb: 2 }}>
          Zonder recht Werving (zien deze vacature niet): {d.zonder_recht.join(', ')}. Geef ze het recht via Admin → Gebruikers.
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2.5, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Zoek op naam" value={q} onChange={(e) => setQ(e.target.value)} sx={{ width: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: STIL }} /></InputAdornment> }} />
        <ToggleButtonGroup size="small" exclusive value={weergave} onChange={(_, v) => v && setWeergave(v)}
          sx={{ '& .MuiToggleButton-root': { borderColor: LIJN, px: 1 } }}>
          <ToggleButton value="kanban" aria-label="Kanban"><ViewKanbanOutlinedIcon fontSize="small" /></ToggleButton>
          <ToggleButton value="tabel" aria-label="Tabel"><TableRowsOutlinedIcon fontSize="small" /></ToggleButton>
        </ToggleButtonGroup>
        {d?.ben_beoordelaar && (
          <FormControlLabel sx={{ ml: 0.5, '& .MuiFormControlLabel-label': { fontSize: 13.5, color: INKT } }}
            control={<Checkbox size="small" checked={alleenMijn} onChange={(e) => setAlleenMijn(e.target.checked)} />}
            label={`Alleen wat ik moet beoordelen (${(d.sollicitaties || []).filter((x) => x.te_beoordelen).length})`} />
        )}
        {(d?.stagetypes || []).length > 0 && (
          <TextField select size="small" label="Stagetype" value={stagetype} onChange={(e) => setStagetype(e.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value="">Alle</MenuItem>
            {d.stagetypes.filter((t) => !t.gearchiveerd || t.sleutel === stagetype).map((t) => <MenuItem key={t.sleutel} value={t.sleutel}>{t.label}</MenuItem>)}
            <MenuItem value="_geen">Stage, nog niet ingedeeld</MenuItem>
            {d.soort === 'open' && <MenuItem value="_overig">Open sollicitatie (geen stage)</MenuItem>}
          </TextField>
        )}
        <Box sx={{ flex: 1 }} />
        {d && !d.kan_bewerken && <Typography sx={{ fontSize: 12.5, color: STIL }}>Je beoordeelt; Joanne verzet de fasen.</Typography>}
      </Box>

      {!d ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={24} /></Box>
        : d.sollicitaties.length === 0 ? (
          <Box sx={{ maxWidth: 520, mx: 'auto', mt: 6, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: INKT }}>Nog geen sollicitaties</Typography>
            <Typography sx={{ mt: 1, color: STIL, fontSize: 14 }}>Reacties op deze vacature uit ikwilwerken@ komen hier vanzelf binnen.</Typography>
          </Box>
        ) : alleenMijn && gefilterd.length === 0 ? (
          <Box sx={{ maxWidth: 520, mx: 'auto', mt: 6, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: INKT }}>Je hebt alles beoordeeld</Typography>
            <Button onClick={() => setAlleenMijn(false)} sx={{ mt: 1, textTransform: 'none', color: ACCENT }}>Alle sollicitaties tonen</Button>
          </Box>
        ) : weergave === 'kanban' ? (
          <DndContext sensors={sensors} onDragEnd={onDragEnd}>
            <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
              {d.fasen.map((f) => (
                <Kolom key={f.sleutel} fase={f} lijst={perFase[f.sleutel] || []} kanSlepen={d.kan_bewerken} onOpen={open} gemarkeerd={gemarkeerd === f.sleutel}
                  stageLabels={stageLabels} />
              ))}
            </Box>
          </DndContext>
        ) : (
          <TableContainer sx={{ bgcolor: '#fff', border: `1px solid ${LIJN}`, borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { color: STIL, fontWeight: 600, fontSize: 12.5 } }}>
                  <TableCell>Naam</TableCell><TableCell>Fase</TableCell><TableCell>Binnen</TableCell>
                  <TableCell>Portfolio</TableCell><TableCell>Beoordeeld</TableCell><TableCell>Jij</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gefilterd.map((s) => (
                  <TableRow key={s.id} hover sx={{ cursor: 'pointer' }} onClick={() => open(s.id)}>
                    <TableCell sx={{ fontWeight: 600, color: INKT }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Avatar naam={s.naam} maat={24} />{s.naam}</Box>
                    </TableCell>
                    <TableCell onClick={(e) => d.kan_bewerken && e.stopPropagation()}>
                      {d.kan_bewerken ? (
                        <TextField select size="small" variant="standard" value={s.fase} onChange={(e) => verzet(s, e.target.value)}
                          InputProps={{ disableUnderline: true }} sx={{ fontSize: 13 }}>
                          {d.fasen.map((f) => <MenuItem key={f.sleutel} value={f.sleutel}>{f.label}</MenuItem>)}
                        </TextField>
                      ) : <FaseChip fase={s.fase} label={d.fasen.find((f) => f.sleutel === s.fase)?.label} />}
                    </TableCell>
                    <TableCell sx={{ color: STIL }}>{dagenGeleden(s.ontvangen_op)}</TableCell>
                    <TableCell sx={{ color: STIL }}>{s.portfolio ? 'ja' : '—'}</TableCell>
                    <TableCell><Signaal s={s} /></TableCell>
                    <TableCell><JouwStatus s={s} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

      <VacatureDialoog open={bewerkOpen} vacature={d} disciplines={disciplines} onClose={() => setBewerkOpen(false)}
        onOpgeslagen={(x) => { setBewerkOpen(false); setD(x); wervingApi.disciplines().then((y) => setDisciplines(y.disciplines || [])).catch(() => {}); }} />
    </Box>
  );
}
